import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import FelixAnalytics from '../felixAnalytics';
import {
  AuthProvider,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  linkWithCredential,
  onAuthStateChanged,
  onIdTokenChanged,
  ParsedToken,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  updateProfile,
  User,
} from 'firebase/auth';
import UsuarioDb from '../db/usuarioDb';
import {
  doc,
  getFirestore,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import TagManager from 'react-gtm-module';
import {
  capitalize,
  chain,
  first,
  isNil,
  join,
  map,
  slice,
  split,
} from 'lodash';
import Usuario from '../models/usuario';
import { Perfil, Role } from '../models/enum';
import Permissoes from '../models/permissoes';
import { Crisp } from 'crisp-sdk-web';
import Functions from '../callable/functions';
import moment from 'moment';

export const UsuarioProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | undefined>();
  const [uid, setUid] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string>();
  const [claims, setClaims] = useState<Claims>();

  useEffect(() => {
    const unsubscribeAuthStateChanges = onAuthStateChanged(
      getAuth(),
      (userAuth) => {
        setIsLoading(true);
        if (userAuth) {
          setUid(userAuth.uid);
          setIsAuthenticated(true);
          setProfilePicture(userAuth.photoURL || undefined);
          setupCrisp(userAuth);
          updateUsuarioUltimoAcesso(userAuth.uid);
        } else {
          setUid(undefined);
          setIsAuthenticated(false);
          setIsLoading(false);
          Crisp.setTokenId();
          Crisp.session.reset();
        }
      },
    );
    const unsubscribeIdTokenChanges = onIdTokenChanged(getAuth(), (user) => {
      user
        ?.getIdTokenResult(true)
        .then((decodedToken) => {
          setClaims(decodedToken.claims);
        })
        .then(unsubscribeIdTokenChanges)
        .finally(() => setIsLoading(false));
    });
    return () => {
      unsubscribeAuthStateChanges();
      unsubscribeIdTokenChanges();
    };
  }, []);

  useEffect(() => {
    if (!!claims) {
      Crisp.session.setData({
        perfil: claims.perfil || '',
        stripeRole: claims.stripeRole || '',
        ...claims.permissoes,
      });
    }
  }, [claims?.perfil, claims?.stripeRole, claims?.permissoes]);

  useEffect(() => {
    if (!!uid) {
      return onSnapshot(new UsuarioDb().doc({ usuarioId: uid }), (snapshot) => {
        setUsuario(snapshot.data());
      });
    } else {
      setUsuario(undefined);
    }
  }, [uid]);

  useEffect(() => {
    if (!!usuario) {
      Crisp.session.onLoaded(() => {
        const usuarioCrispData = chain(usuario)
          .pick([
            'id',
            'business',
            'contaPai',
            'asUser',
            'origem',
            'perfil',
            'cpf',
            'documento',
            'atualizadoAte',
            'atualizadoEm',
            'atualizarPorque',
            'dataInicio',
          ])
          .mapValues((value) => {
            if (moment.isMoment(value)) return value.toISOString();
            return value;
          })
          .pickBy((v) => !isNil(v))
          .value();
        Crisp.session.setData(usuarioCrispData);
      });
      Crisp.chat.onChatInitiated(() => {
        const nome = first(split(usuario.nome, ' ')) || 'aí';
        const isContador = usuario.perfil === Perfil.CONTADOR;

        if (isContador) {
          const cliente = usuario.asUser ? ` (ID: ${usuario.asUser})` : '';
          const msgs = [
            `Olá, ${nome}. Tudo bem?`,
            cliente
              ? `Vi que você está trabalhando no cliente${cliente}. Se a dúvida for sobre outro cliente, é só enviar o ID dele para a gente.`
              : 'Estamos aqui para ajudar com sua operação. Se tiver trabalhando em algum cliente específico, poderia nos enviar o ID dele?',
            'Para conseguirmos te ajudar mais rápido, descreva sua dúvida com o máximo de detalhes possível (incluindo prints, se tiver).',
            'Responderemos assim que possível, normalmente até o próximo dia útil.',
          ];

          msgs.forEach((msg, index) => {
            setTimeout(() => Crisp.message.showText(msg), 1500 * index + 500);
          });
        } else {
          const msgs = [
            `Olá, ${nome}! Tudo bem?`,
            'Qual dúvida ou problema você está enfrentando por aqui hoje?',
            'Para a gente te ajudar mais rápido, pode nos enviar detalhes (como prints de telas ou mensagens de erro)?',
            'Responderemos assim que possível, normalmente até o próximo dia útil. Agradecemos a paciência!',
          ];

          msgs.forEach((msg, index) => {
            setTimeout(() => Crisp.message.showText(msg), 1500 * index + 500);
          });
        }
      });
    }
  }, [!!usuario]);

  return useMemo(
    () => (
      <UsuarioContext.Provider
        value={{
          isLoading,
          isAuthenticated,
          usuario,
          profilePicture,
          claims,
          visualizado: usuario?.asUser ?? usuario?.id,
        }}
      >
        {children}
      </UsuarioContext.Provider>
    ),
    [isLoading, isAuthenticated, usuario, profilePicture, claims],
  );
};

export const UsuarioContext = createContext<{
  isLoading: boolean;
  isAuthenticated: boolean;
  isAnonymous?: boolean;
  usuario?: Usuario;
  visualizado?: string;
  profilePicture?: string;
  claims?: Claims;
}>({
  isLoading: true,
  isAuthenticated: false,
});

export const useUsuario = () => {
  const context = useContext(UsuarioContext);
  return context;
};

function updateUsuarioUltimoAcesso(uid: string) {
  const lastSeenKey = `ultimoAcesso_${uid}`;
  const lastSeen = localStorage.getItem(lastSeenKey);
  const now = Date.now();
  const oneHour = 60 * 60 * 1000;

  if (!lastSeen || now - parseInt(lastSeen, 10) > oneHour) {
    updateDoc(doc(getFirestore(), 'usuarios', uid), {
      ultimoAcesso: serverTimestamp(),
    })
      .then(() => {
        localStorage.setItem(lastSeenKey, now.toString());
      })
      .catch((error) => console.error('Error updating last access:', error));
  }
}

function setupCrisp(userAuth: User) {
  Crisp.load();
  Crisp.setTokenId(userAuth.uid);
  if (!userAuth.isAnonymous) {
    Crisp.user.setEmail(userAuth.email!);
    !!userAuth.displayName && Crisp.user.setNickname(userAuth.displayName);
    !!userAuth.photoURL && Crisp.user.setAvatar(userAuth.photoURL);
  }
  Crisp.session.setData({ usuarioId: userAuth.uid });
}

export function signInWithLink(email: string): Promise<void> {
  return signInWithEmailLink(getAuth(), email, window.location.href)
    .then((authResult) => {
      if (authResult.user) {
        FelixAnalytics.identify(authResult.user.uid);
        FelixAnalytics.track('signin', { via: 'emailLink' });
      }
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

export function signIn(email: string, senha: string): Promise<void> {
  return signInWithEmailAndPassword(getAuth(), email, senha)
    .then((authResult) => {
      if (authResult.user) {
        FelixAnalytics.identify(authResult.user.uid);
        FelixAnalytics.track('signin', { via: 'email' });
      }
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

export function signInWithProvider(
  provider: AuthProvider,
  userAdditionalInfo: Partial<Usuario>,
): Promise<void> {
  return signInWithPopup(getAuth(), provider)
    .then((authResult) => {
      if (getAdditionalUserInfo(authResult) && authResult.user) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'signup-ads',
          },
        });
        FelixAnalytics.identify(authResult.user.uid);
        FelixAnalytics.track('signin', { via: 'google' });
        if (getAdditionalUserInfo(authResult)?.isNewUser)
          return onSignUp(authResult.user, userAdditionalInfo)
            .then(() => new Functions().usuarioCriado({}))
            .then(() => getAuth().currentUser?.getIdTokenResult(true));
      }
    })
    .then(() => {})
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

export async function signUpWithEmail(
  email: string,
  senha: string,
  userAdditionalInfo: Partial<Usuario>,
  business: { nome: string; cnpj: string } | undefined = undefined,
): Promise<User> {
  return await createUserWithEmailAndPassword(getAuth(), email, senha)
    .then((authResult) => {
      if (getAdditionalUserInfo(authResult)?.isNewUser && authResult.user) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'signup-ads',
          },
        });
        FelixAnalytics.identify(authResult.user.uid);
        FelixAnalytics.track('signup', { via: 'email' });
        return onSignUp(authResult.user, userAdditionalInfo)
          .then(() => new Functions().usuarioCriado({ business }))
          .then(() => getAuth().currentUser?.getIdTokenResult(true))
          .then(() => authResult.user);
      } else {
        return authResult.user;
      }
    })
    .catch((error) => {
      console.error(error);
      throw error;
    });
}

export function linkAnonymousUser(
  email: string,
  senha: string,
  userAdditionalInfo: Partial<Usuario>,
): Promise<void> {
  const authCredential = EmailAuthProvider.credential(email, senha);
  return linkWithCredential(getAuth().currentUser!, authCredential).then(
    (userCredential) => {
      onSignUp(userCredential.user, userAdditionalInfo);
      FelixAnalytics.track('link', { via: 'email' });
    },
  );
}

function onSignUp(
  user: User,
  userAdditionalInfo: Partial<Usuario>,
): Promise<void> {
  if (userAdditionalInfo) {
    userAdditionalInfo.nome = userAdditionalInfo.nome || user.displayName || '';
    userAdditionalInfo.email = user.email || '';
    return setDoc(
      new UsuarioDb().doc({ usuarioId: user.uid }),
      {
        ...userAdditionalInfo,
        contato: {
          primeiroNome:
            capitalize(first(split(userAdditionalInfo.nome, ' '))) ?? '',
          ultimoNome:
            join(
              map(slice(split(userAdditionalInfo.nome, ' '), 1), capitalize),
              ' ',
            ) ?? '',
        },
        tour: true,
      },
      { merge: true },
    );
  } else return Promise.resolve();
}

export function atualizarPerfil(
  user: User,
  profileChanges: { displayName?: string | null; photoURL?: string | null },
): Promise<void> {
  return updateProfile(user, profileChanges).then(() =>
    setDoc(
      doc(getFirestore(), `usuarios/${user.uid}`),
      {
        nome: profileChanges.displayName,
        contato: {
          primeiroNome:
            capitalize(first(split(profileChanges.displayName, ' '))) ?? '',
          ultimoNome:
            join(
              map(slice(split(profileChanges.displayName, ' '), 1), capitalize),
              ' ',
            ) ?? '',
        },
      },
      { merge: true },
    ).then(() => getAuth().currentUser?.reload()),
  );
}

export function resetPassword(email: string): Promise<void> {
  return sendPasswordResetEmail(getAuth(), email);
}

export type Claims = ParsedToken & {
  perfil?: Perfil;
  stripeRole?: Role;
  permissoes?: Permissoes;
};
